import {
  Box,
  Group,
  MultiSelect,
  Select,
  Stack,
  TextInput,
  Textarea,
} from '@mantine/core';
import {
  SchemaVariant,
  type SchemaDomain,
  type SchemaRegistry,
  type SchemaType,
} from '@morph-mapper/types';
import { useState } from 'react';
import { StyledSectionTitle } from './styles';
import type { SchemaMetadataFormApi } from '../schema-metadata-form';

type SchemaMetadataFieldsProps = {
  form: SchemaMetadataFormApi;
  registry?: SchemaRegistry;
  readOnly?: boolean;
};

export const SchemaMetadataFields = ({
  form,
  registry,
  readOnly = false,
}: SchemaMetadataFieldsProps) => {
  const [createdOperatorIdOptions, setCreatedOperatorIdOptions] = useState<
    { label: string; value: string }[]
  >([]);

  return (
    <Stack spacing={32}>
      <Box>
        <StyledSectionTitle>Basic Information</StyledSectionTitle>
        <Stack spacing="md">
          <Group grow align="start">
            <form.Field
              name="name"
              children={(field) => (
                <TextInput
                  label="Name"
                  placeholder="Retail XML"
                  value={field.state.value}
                  size="sm"
                  onBlur={field.handleBlur}
                  onChange={(event) =>
                    field.handleChange(event.currentTarget.value)
                  }
                  readOnly={readOnly}
                  required
                />
              )}
            />
            <form.Field
              name="domain"
              children={(field) => (
                <Select
                  label="Domain"
                  data={(registry?.schemaDomains ?? []).map((value) => ({
                    label: value,
                    value,
                  }))}
                  value={field.state.value}
                  size="sm"
                  onBlur={field.handleBlur}
                  onChange={(value) => {
                    const nextDomain = value ?? '';
                    field.handleChange(nextDomain);

                    const rules =
                      registry?.schemaRules?.[nextDomain as SchemaDomain];

                    if (rules?.allowedTypes?.length) {
                      const currentType = form.getFieldValue('type');
                      if (
                        !rules.allowedTypes.includes(currentType as SchemaType)
                      ) {
                        form.setFieldValue('type', rules.allowedTypes[0]);
                      }
                    }

                    if (rules?.allowedVariants?.length) {
                      const currentVariant = form.getFieldValue('variant');
                      if (
                        !rules.allowedVariants.includes(
                          currentVariant as SchemaVariant,
                        )
                      ) {
                        form.setFieldValue('variant', rules.allowedVariants[0]);
                      }
                    }
                  }}
                  allowDeselect={false}
                  disabled={readOnly || !registry}
                />
              )}
            />
          </Group>

          <form.Subscribe
            selector={(state) => state.values.domain}
            children={(domain) => {
              const rules = registry?.schemaRules?.[domain as SchemaDomain];
              const allowedTypes = rules?.allowedTypes;
              const allowedVariants = rules?.allowedVariants;

              return (
                <Group grow align="start">
                  <form.Field
                    name="type"
                    children={(field) => (
                      <Select
                        label="Type"
                        data={(registry?.schemaTypes ?? []).map((value) => ({
                          label: value,
                          value,
                          disabled: allowedTypes
                            ? !allowedTypes.includes(value)
                            : false,
                        }))}
                        value={field.state.value}
                        size="sm"
                        onBlur={field.handleBlur}
                        onChange={(value) =>
                          field.handleChange(value ?? 'standard')
                        }
                        allowDeselect={false}
                        disabled={readOnly || !registry}
                      />
                    )}
                  />
                  <form.Field
                    name="variant"
                    children={(field) => (
                      <Select
                        label="Variant"
                        data={(registry?.schemaVariants ?? []).map((value) => ({
                          label: value,
                          value,
                          disabled: allowedVariants
                            ? !allowedVariants.includes(value)
                            : false,
                        }))}
                        value={field.state.value}
                        size="sm"
                        onBlur={field.handleBlur}
                        onChange={(value) =>
                          field.handleChange(value ?? SchemaVariant.Xml)
                        }
                        allowDeselect={false}
                        disabled={readOnly || !registry}
                      />
                    )}
                  />
                </Group>
              );
            }}
          />

          <form.Field
            name="description"
            children={(field) => (
              <Textarea
                label="Description"
                placeholder="Describe the schema"
                value={field.state.value}
                size="sm"
                onBlur={field.handleBlur}
                onChange={(event) =>
                  field.handleChange(event.currentTarget.value)
                }
                autosize
                minRows={3}
                readOnly={readOnly}
              />
            )}
          />

          <form.Field
            name="operatorId"
            children={(field) => (
              <MultiSelect
                label="Operator IDs"
                placeholder="Add operator IDs"
                description="Create one or more operator IDs for this schema."
                data={Array.from(
                  new Map(
                    [
                      ...createdOperatorIdOptions,
                      ...field.state.value.map((value) => ({
                        label: value,
                        value,
                      })),
                    ].map((option) => [option.value, option]),
                  ).values(),
                )}
                value={field.state.value}
                size="sm"
                searchable
                creatable
                clearable
                disabled={readOnly}
                onBlur={field.handleBlur}
                onChange={field.handleChange}
                getCreateLabel={(query) => `+ Add ${query}`}
                onCreate={(query) => {
                  if (readOnly) {
                    return null;
                  }

                  const value = query.trim();

                  if (!value) {
                    return null;
                  }

                  const existingOption =
                    createdOperatorIdOptions.find(
                      (option) => option.value === value,
                    ) ??
                    field.state.value
                      .map((currentValue) => ({
                        label: currentValue,
                        value: currentValue,
                      }))
                      .find((option) => option.value === value);

                  if (existingOption) {
                    return existingOption;
                  }

                  const item = {
                    label: value,
                    value,
                  };

                  setCreatedOperatorIdOptions((current) => [...current, item]);

                  return item;
                }}
              />
            )}
          />
        </Stack>
      </Box>
    </Stack>
  );
};
